<template>
    <div class="need">
        <div class="need-top">
            <div class="need-title">
                {{ need.title }}
            </div>
            <div class="need-subcontent" :class="{'most-recent': isMostRecent}">
                <div class="need-timeframe">
                    {{ need.created_at | humanDate }}
                </div>
                <div class="need-most-recent" v-if="isMostRecent">
                    most recent need
                </div>
            </div>
            <div class="need-social" v-show="need.is_social">
                <a :href="'mailto:?subject=' + encodeURI(need.title) + '&body=' + encodeURI(need.description)" target="_blank">
                    <img src="/img/social/email.svg" class="social-icon" />
                </a>
                <a :href="'https://www.facebook.com/sharer/sharer.php?u=' + encodeURI(need.url)" target="_blank">
                    <img src="/img/social/facebook.svg" class="social-icon" />
                </a>
                <a :href="'https://twitter.com/intent/tweet?text=' + encodeURI(need.title) + '&url=' + encodeURI(need.url)" target="_blank">
                    <img src="/img/social/twitter.svg" class="social-icon" />
                </a>
            </div>
            <div class="need-description">
                {{ need.description }}
            </div>
            <div class="need-media-container" v-if="need.media && need.media.length">
                <carousel :perPage="1">
                    <slide v-for="media in need.media" :key="media.url">
                        <div v-if="media.mime_type && media.mime_type.startsWith('video/')">
                            <video controls class="need-media-video">
                                <source :src="media.url" :type="media.mime_type" />
                            </video>
                        </div>
                        <div v-else>
                            <img :src="media.url" class="need-media-image" />
                        </div>
                    </slide>
                </carousel>
            </div>
        </div>
        <div class="need-cta">
            <div class="need-cta-title">
                {{ need.cta_title }}
            </div>
            <div class="need-cta-subtext" v-if="need.cta_description">
                {{ need.cta_description }}
            </div>
            <div v-if="need.accepts_donations">
                <div class="cta-button-row mb-20">
                    <div class="cta-button donate-button" @click="donate">
                        Donate Now
                    </div>
                    <div class="cta-donation-success">
                        <span id="donateThankYou" style="display: none;">Thanks for your donation!</span>
                    </div>
                </div>
                <need-progress :goal="need.donation_goal"
                               :donations-total="donationsTotal"
                               v-if="need.donation_goal"></need-progress>
                <div class="cta-donations-total" v-if="!need.donation_goal && donationsTotal">
                    {{ donationsTotal | currency(2) }} donated so far!
                </div>
            </div>
            <div class="mt-20" v-if="need.links">
                <div class="row">
                    <div class="col-xs-6 mb-20" v-for="link in need.links">
                        <need-link class="cta-button" :link="link" :preview="preview"></need-link>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import moment from 'moment';
    import { Carousel, Slide } from 'vue-carousel';
    import NeedLink from './NeedLink';
    import NeedProgress from './NeedProgress';

    export default {
        props: {
            need: {
                type: Object,
                required: true,
            },
            team: {
                type: Object,
                required: true,
            },
            isMostRecent: {
                type: Boolean,
                required: true,
            },
            preview: {
                type: Boolean,
                required: true,
            },
            userDonated: {
                required: false,
            }
        },
        components: { NeedLink, NeedProgress, Carousel, Slide },
        data() {
            return {
                app_url: process.env.MIX_APP_URL,
                donationsTotal: this.need.donations_gross_total ? this.need.donations_gross_total : 0,
            }
        },
        watch: {
            need(need) {
                if(need && need.id) {
                    this.donationsTotal = need.donations_gross_total ? need.donations_gross_total : 0;
                }
            },
            userDonated(time) {
                if(time) {
                    this.flashThankYou();
                }
            }
        },
        filters: {
            humanDate(dateString) {
                let date = moment(dateString);
                let daysAgo = moment().diff(date, 'days');
                if(daysAgo === 0) {
                    return 'today';
                }
                if(daysAgo === 1) {
                    return 'yesterday';
                }
                if(daysAgo < 14) {
                    return daysAgo + ` day${daysAgo > 1 ? 's' : ''} ago`;
                }
                if(daysAgo < 30) {
                    const weeks = moment().diff(date, 'weeks');
                    return  `${weeks} week${weeks > 1 ? 's' : ''} ago`;
                }
                if(daysAgo < 365) {
                    const months = moment().diff(date, 'months');
                    return  `${months} month${months > 1 ? 's' : ''} ago`;
                }
                let yearsDiff = moment().diff(date, 'years');
                let monthsDiff = moment().diff(date, 'months') - (yearsDiff * 12);
                let str = `${yearsDiff} year${yearsDiff > 1 ? 's' : ''}`;
                if(monthsDiff) {
                    str += ` and ${monthsDiff} month${monthsDiff > 1 ? 's' : ''}`;
                }
                return str + ' ago';
            }
        },
        computed: {
            url() {
                if(this.preview) {
                    return this.app_url + '/' + this.team.slug;
                }
                return this.app_url + '/' + this.team.slug + '/' + this.need.slug;
            },
            canAcceptDonations() {
                return this.team.can_accept_donations && this.need.accepts_donations;
            }
        },
        methods: {
            donate() {
                this.$emit('donate', this.need)
            },
            flashThankYou() {
                const $donateText = $("#donateThankYou");
                $donateText.fadeIn(500, function () {
                   setTimeout(() => {
                       $donateText.fadeOut(500);
                   }, 5000);
                });
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import "../../sass/variables";
    @import "../../sass/mixins";

    $needShadow: 0 0 5px 1px rgba(161,161,161,0.75);
    .need {
        border: 1px solid #ABABAB;
        -webkit-box-shadow: $needShadow;
        -moz-box-shadow: $needShadow;
        box-shadow: $needShadow;
        width: 95%;
        max-width: 850px;
        padding: 5px;
        @include media($md) {
            padding: 15px;
        }
    }

    .need-subcontent {
        display: flex;
        margin-bottom: 20px;
        justify-content: flex-start;
        &.most-recent {
            justify-content: space-between;
        }
    }

    .need-top {
        background: #FFFFFF;
        color: #131313;
    }

    .need-title {
        font-size: 24px;
        margin-bottom: 10px;
    }

    .need-timeframe {
        font-size: 12px;
        color: #666666;
    }

    .need-most-recent {
        font-size: 12px;
        color: #666666;
    }

    .need-description {
        font-size: 14px;
        line-height: 24px;
        @include media($md) {
            font-size: 18px;
            line-height: 32px;
            margin: 20px 0 20px 0;
        }
        margin: 10px 0 10px 0;
    }

    .social-icon {
        margin-right: 10px;
        width: 20px;
        @include media($md) {
            margin-right: 15px;
            width: 30px;
        }
    }

    .need-cta {
        background-color: #1d8bf1;
        color: #FFFFFF;
        padding: 20px 10px 10px 10px;
    }

    .need-cta-title {
        font-size: 25px;
        font-weight: 600;
        margin-bottom: 10px;
    }

    .need-cta-subtext {
        font-size: 15px;
        font-weight: 300;
        font-style: italic;
        margin-bottom: 25px;
    }

    .cta-button-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .cta-button {
        border-width: 1px;
        border-style: solid;
        border-color: #FFFFFF;
        padding: 10px 25px;
        text-decoration: none !important;
        text-align: center;
        display: inline-block;
        color: #FFFFFF;
        transition: color 500ms ease-out, border-color 500ms ease-out;
        cursor: pointer;
        &.donate-button {
            font-weight: 600;
            font-size: 16px;
        }
        &:hover {
            color: rgb(255, 141, 5);
            border-color: rgb(255, 141, 5);
        }
        &.disabled {
            border-color: #BBBBBB !important;
            color: #BBBBBB !important;
            pointer-events: none !important;
            cursor: not-allowed !important;
        }
    }

    .cta-donations-total {
        font-size: 14px;
        font-weight: 300;
        margin: 10px 0;
    }

    .cta-donation-success {
        font-size: 18px;
        font-weight: 600;
        color: #EFAE1D;
    }

    .need-media-container {
        margin-bottom: 15px;
    }

    .need-media-video {
        width: 100%;
    }

    .need-media-image {
        width: 100%;
    }
</style>
